import { useCallback, useReducer } from "react";

type CompressorLynxViewStateType = {
    // Options state
    filterText: string;

    // Other state
    isNoData: boolean;
    isOptionsDirty: boolean;
    downloadUid: string | undefined;
    refreshing: boolean;

    summaryListingAlarmAtTop: boolean;
    initialSummaryListingAlarmAtTop?: boolean;

};

const initialState: CompressorLynxViewStateType = {
    filterText: "",
    isNoData: false,
    isOptionsDirty: false,
    downloadUid: undefined,
    refreshing: false,
    summaryListingAlarmAtTop: false,
};

enum CompressorLynxViewActionEnum {
    ResetOptionsRequested = "RESET_OPTIONS_REQUESTED",
    FilterTextChanged = "FILTER_TEXT_CHANGED",
    NoDataChanged = "NO_DATA_CHANGED",
    DownloadUidChanged = 'COMPRESSORLYNX_DOWNLOAD_UID_CHANGED',
    RefreshingChanged = "COMPRESSORLYNX_REFRESHING_CHANGED",
    DisplayAlarmsAtTopChanged = "COMPRESSORLYNX_VIEW_ALARMS_AT_TOP",
}

type CompressorLynxViewActionTypes = {
    type: CompressorLynxViewActionEnum.ResetOptionsRequested;
} | {
    type: CompressorLynxViewActionEnum.FilterTextChanged;
    filterText: string;
} | {
    type: CompressorLynxViewActionEnum.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: CompressorLynxViewActionEnum.RefreshingChanged;
    payload: boolean;
} | {
    type: CompressorLynxViewActionEnum.DisplayAlarmsAtTopChanged;
    payload: boolean;
} | {
    type: CompressorLynxViewActionEnum.NoDataChanged;
    isNoData: boolean;
};


function reducer(state: CompressorLynxViewStateType, action: CompressorLynxViewActionTypes): CompressorLynxViewStateType {

    const copy = { ...state };
    switch (action.type) {

        case CompressorLynxViewActionEnum.ResetOptionsRequested:
            copy.filterText = initialState.filterText;
            copy.isOptionsDirty = false;
            return copy;

        case CompressorLynxViewActionEnum.FilterTextChanged:
            copy.filterText = action.filterText;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case CompressorLynxViewActionEnum.NoDataChanged:
            copy.isNoData = action.isNoData;
            return copy;

        case CompressorLynxViewActionEnum.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case CompressorLynxViewActionEnum.RefreshingChanged:
            copy.refreshing = action.payload;
            return copy;

        case CompressorLynxViewActionEnum.DisplayAlarmsAtTopChanged:
            copy.summaryListingAlarmAtTop = action.payload;
            if (copy.initialSummaryListingAlarmAtTop === undefined) {
                copy.initialSummaryListingAlarmAtTop = action.payload;
            }
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

    
        default:
            return state;
    }
}

function isOptionsDirty(state: CompressorLynxViewStateType) {
    return state.filterText !== initialState.filterText ||
    (state.initialSummaryListingAlarmAtTop !== undefined && state.initialSummaryListingAlarmAtTop !== state.summaryListingAlarmAtTop);
}

type UseCompressorLynxViewStateType = {
    state: CompressorLynxViewStateType;
    resetOptions: () => void;
    setFilterText: (filterText: string) => void;
    setNoData: (isNoData: boolean) => void;
    setDownloadUid: (value: string | undefined) => void;
    setRefreshing: (value: boolean) => void;
    setAlarmsAtTop: (value: boolean) => void;
};

export default function useCompressorLynxViewState(): UseCompressorLynxViewStateType {
    const [state, dispatch] = useReducer(reducer, initialState);

    const resetOptions = useCallback(() => dispatch({
            type: CompressorLynxViewActionEnum.ResetOptionsRequested
        }), []);

    const setFilterText = useCallback((filterText: string) => dispatch({
            type: CompressorLynxViewActionEnum.FilterTextChanged,
            filterText: filterText
        }), []);

    const setNoData = useCallback((isNoData: boolean) => dispatch({
            type: CompressorLynxViewActionEnum.NoDataChanged,
            isNoData: isNoData
        }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: CompressorLynxViewActionEnum.DownloadUidChanged,
        payload: value
    }), []);


    const setRefreshing = useCallback((value: boolean) => dispatch({
        type: CompressorLynxViewActionEnum.RefreshingChanged,
        payload: value
    }), []);

    const setAlarmsAtTop = useCallback((value: boolean) => dispatch({
        type: CompressorLynxViewActionEnum.DisplayAlarmsAtTopChanged,
        payload: value
    }), []);

    
    return {
        state,
        resetOptions,
        setFilterText,
        setNoData,
        setDownloadUid,
        setRefreshing,
        setAlarmsAtTop
    };
}    
